import React, { useEffect, useState } from 'react';
import '../faculty/NotificationsInbox.css';

const StudentNotificationsInbox = ({ studentId }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    limit: 20,
  });

  useEffect(() => {
    fetchNotifications();
  }, [studentId, filter, pagination.current]);

  const fetchNotifications = async () => {
    if (!studentId) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        student_id: studentId,
        unread_only: filter === 'unread',
        limit: pagination.limit,
        offset: (pagination.current - 1) * pagination.limit,
      });

      const response = await fetch(`/api/notifications/student-inbox?${params}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch notifications');
      }

      setNotifications(data.notifications || []);
      setPagination((prev) => ({
        ...prev,
        total: Math.ceil((data.total || 0) / prev.limit),
      }));
    } catch (err) {
      console.error('Error fetching student notifications:', err);
      setError(err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await fetch('/api/notifications/mark-read', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notification_id: notificationId,
          receiver_id: studentId,
          is_faculty: false,
        }),
      });

      if (!response.ok) return;

      setNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === notificationId ? { ...n, is_read: true } : n
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadItems = notifications.filter((n) => !n.is_read);
    for (const n of unreadItems) {
      // Keep sequential calls to avoid race on counter updates.
      await handleMarkAsRead(n.notification_id);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading && notifications.length === 0) {
    return (
      <div className="notifications-inbox">
        <div className="loading-skeleton">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton-item" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-inbox">
      <div className="inbox-header">
        <h2>Notifications</h2>
        <div className="header-controls">
          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            className="filter-select"
          >
            <option value="all">All Notifications</option>
            <option value="unread">Unread Only</option>
          </select>
          {notifications.some((n) => !n.is_read) && (
            <button className="btn-mark-all" onClick={handleMarkAllAsRead}>
              Mark all as read
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      {notifications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">No notifications</div>
          <h3>Nothing new</h3>
          <p>New announcements from faculty will appear here.</p>
        </div>
      ) : (
        <>
          <div className="notifications-list">
            {notifications.map((n) => (
              <div
                key={n.notification_id}
                className={`notification-card ${n.is_read ? 'read' : 'unread'}`}
              >
                <div className="notification-icon">Announcement</div>
                <div className="notification-content">
                  <div className="notification-header">
                    <h4>{n.title}</h4>
                    <span className="timestamp">{formatDate(n.created_at)}</span>
                  </div>
                  <p className="notification-message">{n.message}</p>
                  <div className="notification-footer">
                    <span className="badge-role">From: {n.sender_role}</span>
                    {!n.is_read && (
                      <button
                        className="btn-read"
                        onClick={() => handleMarkAsRead(n.notification_id)}
                      >
                        Mark as read
                      </button>
                    )}
                  </div>
                </div>
                <div className="unread-indicator">
                  {!n.is_read && <div className="unread-dot" />}
                </div>
              </div>
            ))}
          </div>

          {pagination.total > 1 && (
            <div className="pagination">
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, current: prev.current - 1 }))
                }
                disabled={pagination.current === 1}
                className="btn-paginate"
              >
                Previous
              </button>
              <div className="page-info">
                Page {pagination.current} of {pagination.total}
              </div>
              <button
                onClick={() =>
                  setPagination((prev) => ({ ...prev, current: prev.current + 1 }))
                }
                disabled={pagination.current === pagination.total}
                className="btn-paginate"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StudentNotificationsInbox;
